import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";

const PAGE_PARAM = "page";

/** 1-based page from the `page` search param; anything else is page 1. */
export function parsePageParam(raw: string | null): number {
  if (raw === null || !/^\d+$/.test(raw)) {
    return 1;
  }
  return Math.max(1, Number(raw));
}

/** `skip` for a 1-based page. */
export function pageSkip(page: number, pageSize: number): number {
  return (page - 1) * pageSize;
}

/** Page count plus the inclusive 1-based row range shown in the pagination summary. */
export function serverPageWindow(page: number, pageSize: number, total: number) {
  const skip = pageSkip(page, pageSize);
  return {
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
    start: total === 0 ? 0 : Math.min(skip + 1, total),
    end: Math.min(skip + pageSize, total),
  };
}

/** `prev` with the page param set to `page` (page 1 drops it). */
function withPageParam(prev: URLSearchParams, page: number): URLSearchParams {
  const params = new URLSearchParams(prev);
  if (page <= 1) {
    params.delete(PAGE_PARAM);
  } else {
    params.set(PAGE_PARAM, String(page));
  }
  return params;
}

/**
 * Current list page kept in the URL (`?page=N`) so reloads and shared links keep the position.
 * Page 1 drops the param; other search params (filters, focus ids) are preserved.
 * Page changes push a history entry, like the support tickets list.
 */
export function useUrlPage(): [number, (page: number) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const setPage = useCallback(
    (next: number) => {
      // Filters call this on every change; skip no-op navigations so typing in a
      // search box does not stack history entries.
      if (next === page) {
        return;
      }
      setSearchParams((prev) => withPageParam(prev, next));
    },
    [page, setSearchParams],
  );
  return [page, setPage];
}

/**
 * Moves a `?page=` past the end (old link, list shrank) to the last page, replacing the
 * history entry so Back does not return to the empty page. `totalPages` is undefined
 * until a response for the current page has arrived.
 */
export function useClampUrlPage(page: number, totalPages: number | undefined): void {
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (totalPages === undefined || page <= totalPages) {
      return;
    }
    setSearchParams((prev) => withPageParam(prev, totalPages), { replace: true });
  }, [page, totalPages, setSearchParams]);
}
